import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Search, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { useNavigate } from 'react-router-dom';
import { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GlobalSearch = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceTimer = useRef(null);

  useEffect(() => {
    // Keyboard shortcut: Cmd+K or Ctrl+K
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }

    // Debounce search
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/search/quick`, {
          headers: getAuthHeaders(),
          // 08/12 (Josh): 8 hid most of a merchant's deals — 20 is the backend
          // cap; results come back newest deal first.
          params: { q: query, limit: 20 }
        });
        setResults(response.data.results || []);
      } catch (error) {
        console.error('Search failed:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [query, getAuthHeaders]);

  const handleSelect = (result) => {
    setOpen(false);
    setQuery('');
    navigate(result.url);
  };

  const getTypeColor = (type, status) => {
    // Deal-specific: green pill when the deal is paid off (any "Paid in Full" variant
    // — includes "Paid in Full", "Paid in Full (Refinanced)", "Paid in Full / Settled").
    // Blue otherwise (the current/active deal in a merchant's history).
    if (type === 'deal') {
      if (typeof status === 'string' && status.startsWith('Paid in Full')) {
        return 'bg-green-100 text-green-800';
      }
      return 'bg-blue-100 text-blue-800';
    }
    const colors = {
      contact: 'bg-green-100 text-green-800',
      application: 'bg-purple-100 text-purple-800',
      user: 'bg-orange-100 text-orange-800',
      transaction: 'bg-slate-100 text-slate-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <>
      {/* Search trigger button */}
      <div className="relative">
        <button
          onClick={() => setOpen(true)}
          className="flex items-center space-x-2 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors w-64"
        >
          <Search className="h-4 w-4 text-gray-500" />
          <span className="text-gray-500">Search...</span>
          <kbd className="ml-auto pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-white px-1.5 font-mono text-xs font-medium text-gray-500">
            <span className="text-xs">⌘</span>K
          </kbd>
        </button>
      </div>

      {/* Search dialog */}
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Search deals, contacts, references..."
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          {loading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          )}
          
          {!loading && query.length >= 2 && results.length === 0 && (
            <CommandEmpty>No results found.</CommandEmpty>
          )}
          
          {!loading && results.length > 0 && (
            <CommandGroup heading="Results">
              {results.map((result) => (
                <CommandItem
                  key={result.id}
                  value={result.id}
                  onSelect={() => handleSelect(result)}
                  className="flex items-start space-x-3 py-3 cursor-pointer"
                >
                  <span
                    data-testid={`global-search-result-pill-${result.type}-${result.id}`}
                    className={`px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(result.type, result.status)}`}
                  >
                    {result.type}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{result.label}</div>
                    {result.sublabel && (
                      <div className="text-xs text-gray-500 mt-0.5">{result.sublabel}</div>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
};

export default GlobalSearch;
